import { FC, useMemo, useCallback } from "react"
import classNames from "classnames"

// components
import { Icon } from "../../../Icon/Icon"

// types
import { SearchResultsData } from "../../SearchResultsTemplate"
import { ChevronLeftIcon, ChevronRightIcon } from "@eventbrite/marmalade"

// styles
import * as styles from "./PaginationList.module.scss"

export const PaginationList: FC<{
  paginationIndex: number
  setPaginationIndex: React.Dispatch<React.SetStateAction<number>>
  data: SearchResultsData[]
}> = ({ paginationIndex, setPaginationIndex, data }) => {
  if (!data || data?.length <= 1) {
    return null
  }

  const maximumLength = useMemo(() => {
    return data?.length
  }, [data])

  const handleNumClick = useCallback(
    (newPaginationNumber: number) => {
      setPaginationIndex(newPaginationNumber)
    },
    [setPaginationIndex]
  )

  const numbersToRender = useMemo(() => {
    if (data?.length > 1) {
      const finalData = []
      const maximumIndex = data.length - 1
      const currentValue = paginationIndex + 1

      if (data?.length > 2) {
        data.forEach((x) => {
          if (
            x?.paginationData?.index !== 0 &&
            x?.paginationData?.index !== maximumIndex
          ) {
            return finalData.push(
              <PaginationItem
                key={x?.paginationData?.index}
                index={x?.paginationData?.index}
                handleNumClick={handleNumClick}
                active={x?.paginationData?.index === paginationIndex}
              >
                {x?.paginationData?.value}
              </PaginationItem>
            )
          }
        })
      }

      // add first item
      finalData.unshift(
        <PaginationItem
          key={data[0]?.paginationData?.index}
          active={paginationIndex === data[0]?.paginationData?.index}
          handleNumClick={handleNumClick}
          index={data[0]?.paginationData?.index}
        >
          {data[0]?.paginationData?.value}
        </PaginationItem>
      )

      // add last item
      finalData.push(
        <PaginationItem
          key={data[data?.length - 1]?.paginationData?.index}
          index={data[data?.length - 1]?.paginationData?.index}
          handleNumClick={handleNumClick}
          active={maximumIndex === paginationIndex}
        >
          {maximumLength}
        </PaginationItem>
      )

      if (finalData?.length >= 6) {
        const gapBeginning = currentValue
        const gapEnding = maximumIndex + 1 - currentValue

        if (currentValue <= 2) {
          finalData.splice(
            3,
            finalData.length - 4,
            <PaginationItem
              key="...__3"
              active={false}
              className={styles?.disabled}
            >
              ...
            </PaginationItem>
          )
        } else if (currentValue >= 3 && gapBeginning >= 2 && gapEnding >= 2) {
          finalData.splice(
            1,
            1,
            <PaginationItem
              key="...__1"
              active={false}
              className={styles?.disabled}
            >
              ...
            </PaginationItem>
          )

          finalData.splice(
            2,
            1,
            <PaginationItem
              key={`index__${currentValue - 1}`}
              handleNumClick={handleNumClick}
              index={currentValue - 1}
              active={currentValue - 1 === paginationIndex}
            >
              {currentValue}
            </PaginationItem>
          )

          finalData.splice(
            3,
            finalData.length - 4,
            <PaginationItem
              key="...__3"
              active={false}
              className={styles?.disabled}
            >
              ...
            </PaginationItem>
          )
        } else if (currentValue > 3) {
          finalData.splice(
            1,
            finalData.length - 4,
            <PaginationItem
              key={`...__${data.length - 3}`}
              active={false}
              className={styles?.disabled}
            >
              ...
            </PaginationItem>
          )
        }
      }

      return finalData
    }

    return null
  }, [data, maximumLength, paginationIndex])

  const handlePaginationClick = useCallback(
    (operation: "backward" | "forward") => {
      if (operation === "forward" && paginationIndex < maximumLength - 1) {
        setPaginationIndex(paginationIndex + 1)
      }

      if (operation === "backward" && paginationIndex > 0) {
        setPaginationIndex(paginationIndex - 1)
      }
    },
    [paginationIndex, setPaginationIndex, numbersToRender]
  )

  return (
    <>
      <span
        className={styles?.paginationArrowContainer}
        onClick={() => handlePaginationClick("backward")}
      >
        <Icon
          className={classNames(styles?.leftArrow, {
            [styles?.disabled]: paginationIndex === 0
          })}
          icon={<ChevronLeftIcon size="medium" />}
        />
      </span>

      {numbersToRender}

      <span
        className={styles?.paginationArrowContainer}
        onClick={() => handlePaginationClick("forward")}
      >
        <Icon
          className={classNames(styles?.rightArrow, {
            [styles?.disabled]: data?.length - 1 === paginationIndex
          })}
          icon={<ChevronRightIcon size="medium" />}
        />
      </span>
    </>
  )
}

const PaginationItem: FC<{
  active: boolean
  children: React.ReactNode
  handleNumClick?: (newPaginationNumber: number) => void
  index?: number
  className?: string
}> = ({ index, handleNumClick, active, children, className }) => {
  return (
    <span
      onClick={() =>
        handleNumClick
          ? handleNumClick(typeof index === "number" ? index : 0)
          : null
      }
      className={classNames(
        styles?.numberSpan,
        {
          [styles?.active]: active
        },
        className
      )}
    >
      {children}
    </span>
  )
}
